import { yupResolver } from '@hookform/resolvers/yup'
import InfoBlock from 'components/UI/Blocks/InfoBlock'
import Button from 'components/UI/Buttons/Button'
import MaskedInput from 'components/UI/Inputs/MaskedInput'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import { useAppDispatch, useAppSelector } from 'hooks'
import { useCreditDecision } from 'hooks/useCreditDecision'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRequestThirdPledgerMutation } from 'redux/api/3rdPledgerApi'
import { getThirdPartyPledgerSelector } from 'redux/store/slices/statusSlice'
import { getActiveCreditApplication } from 'redux/store/slices/userSlice'
import {
  getThirdPartyNotificationsSelector,
  removeStepNotificationsData,
  removeThirdPartyNotificationData
} from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import tw from 'twin.macro'
import Emitter from 'utils/emitter'
import { IINMask, PhoneMask } from 'utils/i-masks'

import { type ThirdPartyPledgeFormValues, thirdPartyPledgeFormSchema } from './schemas'

const thirdPartyPledgerConfirmationSteps = [
  FLOW_STEPS.AWAITING_THIRD_PARTY_PLEDGE_CONFIRMATION,
  FLOW_STEPS.FETCH_THIRD_PARTY_PLEDGER_PERSONAL_INFO,
  FLOW_STEPS.GENERATE_THIRD_PARTY_PLEDGER_DOCUMENTS_SIGNED_BY_OTP,
  FLOW_STEPS.THIRD_PARTY_PLEDGER_PRE_SCORE,
  FLOW_STEPS.THIRD_PARTY_PLEDGER_BIOMETRY_FORENSICS,
  FLOW_STEPS.THIRD_PARTY_PLEDGER_SCORING,
  FLOW_STEPS.FETCH_THIRD_PARTY_PLEDGER_PKB_REPORT,
  FLOW_STEPS.THIRD_PARTY_PROPERTY_PLEDGES,
  FLOW_STEPS.AWAITING_THIRD_PARTY_CONDITIONS_CONFIRMATION,
  FLOW_STEPS.AWAITING_THIRD_PARTY_PROPERTY_CHOICE
]

const AddThirdPartyPledger = ({ isThirdPartyFlowDone }: { isThirdPartyFlowDone: boolean }) => {
  // Tools
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  // Selectors
  const { id: creditID } = useAppSelector(getActiveCreditApplication)
  const thirdPartyPledger = useAppSelector(getThirdPartyPledgerSelector)
  const { flow_status: pledgerFlowStatus } = useAppSelector(getThirdPartyNotificationsSelector)

  // Mutations
  const [triggerRequest3rdPledger, { isLoading: isPledgerRequestLoading }] = useRequestThirdPledgerMutation()

  // Custom hooks
  const { is3rdPartyPledgerApplied } = useCreditDecision()

  // Forms
  const {
    control,
    formState: { isValid },
    handleSubmit
  } = useForm<ThirdPartyPledgeFormValues>({
    defaultValues: {
      iin: '',
      phone: ''
    },
    resolver: yupResolver(thirdPartyPledgeFormSchema)
  })

  // Handlers
  const requestThirdPartyPledger = async ({ iin, phone }: ThirdPartyPledgeFormValues) => {
    if (!creditID) return console.error('there is no credit id')
    try {
      dispatch(removeThirdPartyNotificationData())
      await triggerRequest3rdPledger({
        credit_application_id: creditID,
        iin,
        mobile_phone: `+${phone}`
      }).unwrap()
      Emitter.emit(ApplicationEvents.FETCH_FLOW_IDS_AND_NAVIGATE)
    } catch (error) {
      console.log(error)
    }
  }

  // Effects
  // Clear notifications if 3rd party pledger rejected application
  useEffect(() => {
    if (pledgerFlowStatus !== 'REJECTED') return
    dispatch(removeStepNotificationsData(thirdPartyPledgerConfirmationSteps))
  }, [dispatch, pledgerFlowStatus])

  return (
    <InfoBlock twStyle={tw`flex flex-col gap-y-5`}>
      <div tw='space-y-2'>
        <Heading variant='subtitle' twStyle={tw`leading-5`}>
          {isThirdPartyFlowDone ? t('3rd-party-pledger.title-done') : t('3rd-party-pledger.title')}
        </Heading>
        <Typography twStyle={tw`text-grey-dark`} variant='subbody'>
          {isThirdPartyFlowDone ? t('3rd-party-pledger.subtitle-done') : t('3rd-party-pledger.subtitle')}
        </Typography>
      </div>
      <div tw='space-y-1'>
        <Typography twStyle={tw`leading-5`} variant='subbody'>
          {t('3rd-party-pledger.enter-pledger-info')}
        </Typography>
        <div tw='flex gap-4 flex-col md:flex-row'>
          <MaskedInput
            name='phone'
            placeholder={thirdPartyPledger ? thirdPartyPledger.phone : '+7 (---) --- -- --'}
            label={t('flow-mobile-number')}
            control={control}
            disabled={is3rdPartyPledgerApplied || isPledgerRequestLoading}
            maskOptions={{
              mask: PhoneMask
            }}
          />
          <MaskedInput
            name='iin'
            placeholder={thirdPartyPledger ? thirdPartyPledger.iin : '000000000000'}
            label={t('flow-iin')}
            disabled={is3rdPartyPledgerApplied || isPledgerRequestLoading}
            control={control}
            maskOptions={IINMask}
          />
        </div>
      </div>
      {isThirdPartyFlowDone ? null : is3rdPartyPledgerApplied ? (
        <div tw='space-y-2'>
          <Typography variant='subbody' weight='bold'>
            {t('3rd-party-pledger.sms-sended')}
          </Typography>
          <Typography variant='subbody' twStyle={tw`text-grey-dark`}>
            {t('3rd-party-pledger.please-wait')}
          </Typography>
        </div>
      ) : (
        <Button
          variant='outline'
          disabled={!isValid || is3rdPartyPledgerApplied}
          loading={isPledgerRequestLoading}
          onClick={handleSubmit(requestThirdPartyPledger)}
        >
          <Typography weight='bold'>{t('3rd-party-pledger.send-btn-text')}</Typography>
        </Button>
      )}
    </InfoBlock>
  )
}

export default AddThirdPartyPledger
